"use client"

import Image from "next/image"
import { CheckCircle2 } from "lucide-react"

type MVAboutSection = {
  id: number
  eyebrow: string
  title: string
  subtitle: string
  description: string
  bullets: string[]
  image: string
  imagePosition: "left" | "right"
}

const mvSections: MVAboutSection[] = [
  {
    id: 1,
    eyebrow: "Our Legacy",
    title: "M.V. Tutorials Advantage",
    subtitle: "Leading students to academic excellence since 1999",
    description:
      "At M.V. Tutorials, learners are mentored by experienced faculty who combine concept clarity with exam strategy. Our programs foster disciplined study habits while encouraging curiosity and analytical thinking.",
    bullets: [
      "Integrated classrooms for board and entrance preparation",
      "Small batches for personalised attention and doubt-solving",
      "Structured mentoring with progress dashboards for parents",
    ],
    image: "/images/institution-building.webp",
    imagePosition: "left",
  },
  {
    id: 2,
    eyebrow: "How We Teach",
    title: "Data-Driven Learning",
    subtitle: "Adaptive schedules tailored to individual needs",
    description:
      "We map student progress through weekly diagnostics, chapter-wise tests, and mock exams. From fundamentals to advanced problem solving, each child receives targeted support to close learning gaps quickly.",
    bullets: [
      "Daily practice papers aligned with latest exam trends",
      "AI-supported analytics for performance insights",
      "Dedicated revision bootcamps and power sessions",
    ],
    image: "/images/institution-building.webp",
    imagePosition: "right",
  },
  {
    id: 3,
    eyebrow: "Beyond Scores",
    title: "Career Readiness",
    subtitle: "Mentoring future professionals and innovators",
    description:
      "From counselling workshops to alumni sessions, we help students explore diverse careers. Our soft-skill labs focus on communication, interview prep, and time management for real-world success.",
    bullets: [
      "Guest lectures from industry experts and alumni achievers",
      "College selection support and application guidance",
      "Life skills sessions on collaboration and resilience",
    ],
    image: "/images/managing-director.webp",
    imagePosition: "left",
  },
]

export function MVAboutUsOverview() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white via-[#f2f6ff] to-white py-16 sm:py-20 lg:py-24">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle,rgba(30,64,175,0.12)_1px,transparent_1px)] [background-size:26px_26px]" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-16 max-w-3xl text-center">
          <span className="inline-flex items-center justify-center rounded-full border border-blue-200 bg-white px-5 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-blue-700 shadow-sm">
            M.V. Tutorials
          </span>
          <h2 className="mt-6 text-3xl font-serif font-bold leading-tight text-[#0f172a] sm:text-4xl md:text-5xl">
            Transforming dedication into distinction
          </h2>
          <p className="mt-4 text-base text-[#1e293b]/80 sm:text-lg">
            Discover how MV Tutorials nurtures disciplined study routines, exam mastery, and confident career pathways for ambitious learners.
          </p>
        </div>

        <div className="space-y-16 sm:space-y-20 lg:space-y-24">
          {mvSections.map((section) => {
            const ImageBlock = (
              <div className="relative overflow-hidden rounded-[36px] border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-white shadow-[0px_24px_55px_rgba(30,64,175,0.14)]">
                <div className="relative aspect-[5/4]">
                  <Image
                    src={section.image}
                    alt={section.title}
                    fill
                    className="object-cover"
                    sizes="(min-width: 768px) 50vw, 90vw"
                    priority={section.id === 1}
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/10 via-transparent to-transparent" />
              </div>
            )

            const ContentBlock = (
              <div className="relative">
                <span className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.3em] text-blue-600">
                  {section.eyebrow}
                </span>
                <h3 className="mt-5 text-2xl font-serif font-bold text-[#0f172a] sm:text-3xl">
                  {section.title}
                </h3>
                <p className="mt-3 text-base font-semibold text-blue-700 sm:text-lg">
                  {section.subtitle}
                </p>
                <p className="mt-4 text-base leading-relaxed text-[#1f2937]/85 sm:text-lg">
                  {section.description}
                </p>

                <ul className="mt-6 space-y-3">
                  {section.bullets.map((bullet, index) => (
                    <li key={index} className="flex items-start gap-3 text-base text-[#27324d]">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-500" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )

            return (
              <div key={section.id} className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
                {section.imagePosition === "left" ? (
                  <>
                    {ImageBlock}
                    {ContentBlock}
                  </>
                ) : (
                  <>
                    {ContentBlock}
                    {ImageBlock}
                  </>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

